import React, { useState, useEffect } from 'react';
import { documentAPI, wardAPI, mediaAPI, projectAPI } from '../services/api';
import { Document, Ward, Media, Project } from '../types';

interface WardBudget {
  id: string;
  name: string;
  county: string;
  population: number;
  budget_allocation: number;
  mca: string;
  projects_count: number;
  created_at: string;
}

interface BudgetSummary {
  total_wards: number;
  total_allocated: number;
  county: string;
  average_allocation: number;
}

const DocumentsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [media, setMedia] = useState<Media[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);
  const [wardBudgets, setWardBudgets] = useState<WardBudget[]>([]);
  const [budgetSummary, setBudgetSummary] = useState<BudgetSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'documents' | 'media' | 'budgets' | 'projects'>('documents');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    sector: '',
    ward: '',
    type: '',
    fiscal_year: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [docsRes, wardsRes, mediaRes, projectsRes] = await Promise.all([
        documentAPI.getAll(),
        wardAPI.getByCounty('Turkana'),
        mediaAPI.getAll(),
        projectAPI.getPublic('Turkana')
      ]);
      
      setDocuments(docsRes.data || []);
      setWards(wardsRes.data || []);
      setMedia(mediaRes.data || []);
      setProjects(projectsRes.data || []);
      
      // Load ward budget data - only wards with actual budgets from Excel
      try {
        const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const budgetRes = await fetch(`${API_BASE}/wards/budgets?county=Turkana`);
        if (budgetRes.ok) {
          const budgetData = await budgetRes.json();
          console.log('📊 Ward budgets loaded:', budgetData);
          setWardBudgets(budgetData.wards || []);
          setBudgetSummary(budgetData.summary || null);
        } else {
          console.warn('Failed to load ward budgets');
          setWardBudgets([]);
          setBudgetSummary(null);
        }
      } catch (budgetError) {
        console.error('Failed to load budget data:', budgetError);
        setWardBudgets([]);
        setBudgetSummary(null);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const parseFiscalYear = (title: string): string | null => {
    const m = title.match(/FY\s*(\d{4})\s*[-/]\s*(\d{2,4})/i);
    if (m) {
      const start = m[1];
      const end = m[2].length === 4 ? m[2] : start.slice(0, 2) + m[2];
      return `${start}/${end}`;
    }
    const m2 = title.match(/(\d{4})[/-](\d{4})/);
    return m2 ? `${m2[1]}/${m2[2]}` : null;
  };

  const availableFiscalYears = Array.from(
    new Set(documents.map(d => parseFiscalYear(d.title)).filter(Boolean))
  ).sort((a, b) => b!.localeCompare(a!)) as string[];

  const availableTypes = Array.from(new Set(documents.map(d => d.type).filter(Boolean))).sort();
  const availableSectors = Array.from(new Set(documents.map(d => d.sector).filter(Boolean))).sort();

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSector = !filters.sector || doc.sector === filters.sector;
    const matchesWard = !filters.ward || doc.ward === filters.ward;
    const matchesType = !filters.type || doc.type === filters.type;
    const matchesFiscalYear = !filters.fiscal_year || parseFiscalYear(doc.title) === filters.fiscal_year;
    return matchesSearch && matchesSector && matchesWard && matchesType && matchesFiscalYear;
  });

  // All documents are real uploaded files - no virtual entries
  const officialDocuments = filteredDocuments;

  const getDocumentIcon = (type: string) => {
    switch (type) {
      case 'budget': return '💰';
      case 'plan': return '📋';
      case 'report': return '📊';
      case 'policy': return '📜';
      default: return '📄';
    }
  };

  const getFileUrl = (relativeUrl: string) => {
    if (relativeUrl.startsWith('http')) return relativeUrl;
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const baseUrl = API_BASE.replace(/\/api$/, '');
    return `${baseUrl}${relativeUrl}`;
  };

  const canViewInBrowser = (fileUrl: string, fileFormat?: string) => {
    const viewable = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'mp4', 'webm', 'mp3', 'wav'];
    const ext = (fileFormat || fileUrl.split('.').pop() || '').toLowerCase();
    return viewable.includes(ext);
  };

  const handleView = async (fileUrl: string, fileFormat?: string, originalFilename?: string) => {
    const fullUrl = getFileUrl(fileUrl);
    try {
      const res = await fetch(fullUrl);
      if (!res.ok) throw new Error('not found');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = originalFilename || fileUrl.split('/').pop() || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch {
      alert('⚠️ File not available. It may need to be re-uploaded.');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) return <div className="loading">Loading documents...</div>;

  return (
    <div className="documents-page">
      <header className="page-header">
        <div className="icon icon-primary">📚</div>
        <h1>Document & Media Repository</h1>
        <p>Access official county documents, photos, and videos of project engagements</p>
        <button 
          onClick={loadData} 
          className="refresh-btn"
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          🔄 Refresh Data
        </button>
      </header>

      <section className="tab-navigation">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            📄 Documents ({documents.length})
          </button>
          <button 
            className={`tab ${activeTab === 'budgets' ? 'active' : ''}`}
            onClick={() => setActiveTab('budgets')}
          >
            💰 Ward Budgets ({wardBudgets.length})
          </button>
          <button 
            className={`tab ${activeTab === 'projects' ? 'active' : ''}`}
            onClick={() => setActiveTab('projects')}
          >
            🏗️ Projects ({projects.length})
          </button>
          <button 
            className={`tab ${activeTab === 'media' ? 'active' : ''}`}
            onClick={() => setActiveTab('media')}
          >
            📸 Media ({media.length})
          </button>
        </div>
      </section>

      <section className="search-filters">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search documents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filters">
          <div className="filter-group">
            <label>Document Type:</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters({...filters, type: e.target.value})}
            >
              <option value="">All Types</option>
              {availableTypes.map(t => (
                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Budget Year:</label>
            <select
              value={filters.fiscal_year}
              onChange={(e) => setFilters({...filters, fiscal_year: e.target.value})}
            >
              <option value="">All Years</option>
              {availableFiscalYears.map(fy => (
                <option key={fy} value={fy}>{fy}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Sector:</label>
            <select
              value={filters.sector}
              onChange={(e) => setFilters({...filters, sector: e.target.value})}
            >
              <option value="">All Sectors</option>
              {availableSectors.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Ward:</label>
            <select
              value={filters.ward}
              onChange={(e) => setFilters({...filters, ward: e.target.value})}
            >
              <option value="">All Wards</option>
              {wards.map(ward => (
                <option key={ward.id} value={ward.name}>{ward.name}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {activeTab === 'documents' && (
        <>
          <section className="document-categories">
            <div className="section-header">
              <h2>Document Categories</h2>
            </div>
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">💰</div>
                <div className="feature-content">
                  <h3>Budget Documents</h3>
                  <p>County budgets, fiscal strategy papers, and financial reports</p>
                  <span className="feature-status">{documents.filter(d => d.type === 'budget').length} documents</span>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon">📋</div>
                <div className="feature-content">
                  <h3>Development Plans</h3>
                  <p>County integrated development plans and sector plans</p>
                  <span className="feature-status">{documents.filter(d => d.type === 'plan').length} documents</span>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon">📊</div>
                <div className="feature-content">
                  <h3>Implementation Reports</h3>
                  <p>Implementation reports and project progress updates</p>
                  <span className="feature-status">{documents.filter(d => d.type === 'report').length} documents</span>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon">📜</div>
                <div className="feature-content">
                  <h3>Policy Documents</h3>
                  <p>Turkana County policies, Acts and Regulations</p>
                  <span className="feature-status">{documents.filter(d => d.type === 'policy').length} documents</span>
                </div>
              </div>
            </div>
          </section>

          <section className="documents-list">
            <div className="list-header">
              <h2>Documents ({filteredDocuments.length})</h2>
            </div>

            {filteredDocuments.length === 0 ? (
              <div className="no-documents">
                <p>No documents found matching your search criteria.</p>
              </div>
            ) : (
              <>
                {officialDocuments.length > 0 && (
                  <>
                    <h3>Official Documents</h3>
                    <div className="documents-grid">
                      {officialDocuments.map(document => (
                        <div key={document.id} className="document-card">
                          <div className="document-header">
                            <div className="document-icon">
                              {getDocumentIcon(document.type)}
                            </div>
                            <div className="document-meta">
                              <span className="document-type">{document.type.toUpperCase()}</span>
                              <span className="document-date">{new Date(document.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>

                          <div className="document-content">
                            <h3>{document.title}</h3>
                            <p>{document.description}</p>
                            
                            <div className="document-tags">
                              {document.sector && <span className="tag sector-tag">{document.sector}</span>}
                              {document.ward && <span className="tag ward-tag">{document.ward}</span>}
                            </div>

                            <div className="document-info">
                              <span className="file-size">{formatFileSize(document.file_size || 0)}</span>
                              <span className="file-format">{document.file_format?.toUpperCase()}</span>
                            </div>
                          </div>

                          <div className="document-actions">
                            <button
                              onClick={() => handleView(document.file_url, document.file_format, (document as any).original_filename)}
                              className="view-btn"
                            >
                              {canViewInBrowser(document.file_url, document.file_format) ? '⬇️ Download' : '⬇️ Open/Download'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </section>
        </>
      )}

      {activeTab === 'budgets' && (
        <>
          <section className="budget-overview">
            <div className="section-header">
              <h2>Ward Budget Allocations</h2>
              <p>Budget allocations extracted from uploaded Excel documents (summed from project allocations)</p>
            </div>
            
            {budgetSummary && budgetSummary.total_wards > 0 ? (
              <div className="budget-summary">
                <div className="stats-grid">
                  <div className="stat-card">
                    <h3><div className="icon icon-sm icon-primary">🏘️</div>Total Wards</h3>
                    <p className="stat-value">{budgetSummary.total_wards}</p>
                  </div>
                  <div className="stat-card">
                    <h3><div className="icon icon-sm icon-success">💰</div>Total Allocated</h3>
                    <p className="stat-value">KES {(budgetSummary.total_allocated / 1e9).toFixed(2)}B</p>
                  </div>
                  <div className="stat-card">
                    <h3><div className="icon icon-sm icon-warning">📈</div>Average per Ward</h3>
                    <p className="stat-value">KES {(budgetSummary.average_allocation / 1e6).toFixed(1)}M</p>
                  </div>
                  <div className="stat-card">
                    <h3><div className="icon icon-sm icon-danger">🏢</div>County</h3>
                    <p className="stat-value">{budgetSummary.county}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-budgets">
                <p>No ward budget data available yet.</p>
              </div>
            )}
          </section>

          <section className="ward-budgets-list">
            <div className="list-header">
              <h2>Ward Budget Details ({wardBudgets.length})</h2>
            </div>

            {wardBudgets.length === 0 ? (
              <div className="no-budgets">
                <div className="empty-state">
                  <div className="empty-icon">📊</div>
                  <h3>No Ward Budget Data Available</h3>
                  <p>Ward budgets are extracted from Excel files by summing all project allocations per ward.</p>
                  <div className="upload-hint">
                    <p><strong>How it works:</strong></p>
                    <ol>
                      <li>Institutional Responder uploads Excel file with "Total Ward Projects" sheet</li>
                      <li>System finds Ward column and Allocation/Budget column</li>
                      <li>Sums all project allocations per ward (e.g., KIBISH = KES 60,000,000)</li>
                      <li>Updates ward budgets with actual totals</li>
                      <li>Only wards with projects appear here</li>
                    </ol>
                    <p><strong>Example:</strong> If KIBISH ward has 3 projects worth 20M, 25M, and 15M, the total ward budget will be 60M.</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="budgets-grid">
                {wardBudgets.map(ward => (
                  <div key={ward.id} className="budget-card">
                    <div className="budget-header">
                      <div className="ward-icon">
                        🏘️
                      </div>
                      <div className="ward-info">
                        <h3>{ward.name} Ward</h3>
                        <p>MCA: {ward.mca}</p>
                      </div>
                      <div className="budget-amount">
                        <span className="amount">KES {(ward.budget_allocation / 1e6).toFixed(1)}M</span>
                      </div>
                    </div>

                    <div className="budget-details">
                      <div className="detail-row">
                        <span className="label">Population:</span>
                        <span className="value">{ward.population.toLocaleString()}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Projects:</span>
                        <span className="value">{ward.projects_count || 0} projects</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Per Capita:</span>
                        <span className="value">KES {Math.round(ward.budget_allocation / ward.population).toLocaleString()}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Source:</span>
                        <span className="value">Excel Upload</span>
                      </div>
                    </div>

                    <div className="budget-progress">
                      <div className="progress-label">
                        <span>Allocation vs Average</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: `${Math.min(100, (ward.budget_allocation / (budgetSummary?.average_allocation || 1)) * 100)}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {activeTab === 'media' && (
        <>
          <section className="media-summary-cards">
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <div className="stat-card" style={{ flex: 1, minWidth: '160px' }}>
                <h3>📸 Photos</h3>
                <p className="stat-value">{media.filter(m => m.media_type === 'photo').length}</p>
                <p style={{ fontSize: '0.85rem', color: '#666' }}>Project documentation</p>
              </div>
              <div className="stat-card" style={{ flex: 1, minWidth: '160px' }}>
                <h3>🎥 Videos</h3>
                <p className="stat-value">{media.filter(m => m.media_type === 'video').length}</p>
                <p style={{ fontSize: '0.85rem', color: '#666' }}>Community engagements</p>
              </div>
              <div className="stat-card" style={{ flex: 1, minWidth: '160px' }}>
                <h3>📁 Total</h3>
                <p className="stat-value">{media.length}</p>
                <p style={{ fontSize: '0.85rem', color: '#666' }}>All media files</p>
              </div>
            </div>
          </section>

          {media.length === 0 ? (
            <div className="no-media" style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              <div style={{ fontSize: '3rem' }}>📭</div>
              <p>No media files available yet.</p>
            </div>
          ) : (
            <>
              {media.filter(m => m.media_type === 'photo').length > 0 && (
                <section style={{ marginBottom: '2.5rem' }}>
                  <h2 style={{ marginBottom: '1rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.5rem' }}>📸 Project Photos</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
                    {media.filter(m => m.media_type === 'photo').map(item => (
                      <article key={item.id} style={{ borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', background: '#fff' }}>
                        <div style={{ position: 'relative', paddingTop: '60%', background: '#f0f0f0', overflow: 'hidden' }}>
                          <img
                            src={getFileUrl(item.file_url)}
                            alt={item.title}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                            onClick={() => window.open(getFileUrl(item.file_url), '_blank')}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        </div>
                        <div style={{ padding: '1rem' }}>
                          <h3 style={{ margin: '0 0 0.4rem', fontSize: '1rem' }}>{item.title}</h3>
                          {item.description && <p style={{ margin: '0 0 0.6rem', fontSize: '0.85rem', color: '#555' }}>{item.description}</p>}
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                              {item.county && <span className="tag sector-tag">{item.county}</span>}
                              {item.ward_id && <span className="tag ward-tag">{wards.find(w => w.id === item.ward_id)?.name}</span>}
                            </div>
                            <span style={{ fontSize: '0.75rem', color: '#999' }}>{new Date(item.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              )}

              {media.filter(m => m.media_type === 'video').length > 0 && (
                <section style={{ marginBottom: '2.5rem' }}>
                  <h2 style={{ marginBottom: '1rem', borderBottom: '2px solid #e0e0e0', paddingBottom: '0.5rem' }}>🎥 Engagement Videos</h2>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.5rem' }}>
                    {media.filter(m => m.media_type === 'video').map(item => (
                      <article key={item.id} style={{ borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', background: '#fff' }}>
                        <video
                          controls
                          style={{ width: '100%', maxHeight: '220px', background: '#000', display: 'block' }}
                          preload="metadata"
                        >
                          <source src={getFileUrl(item.file_url)} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                        <div style={{ padding: '1rem' }}>
                          <h3 style={{ margin: '0 0 0.4rem', fontSize: '1rem' }}>{item.title}</h3>
                          {item.description && <p style={{ margin: '0 0 0.6rem', fontSize: '0.85rem', color: '#555' }}>{item.description}</p>}
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                              {item.county && <span className="tag sector-tag">{item.county}</span>}
                              {item.ward_id && <span className="tag ward-tag">{wards.find(w => w.id === item.ward_id)?.name}</span>}
                            </div>
                            <span style={{ fontSize: '0.75rem', color: '#999' }}>{new Date(item.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </>
      )}

      {activeTab === 'projects' && (
        <>
          <section className="projects-overview">
            <div className="section-header">
              <h2>Extracted Projects</h2>
              <p>Projects automatically extracted from uploaded Excel budget documents</p>
            </div>
            
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">🏗️</div>
                <div className="feature-content">
                  <h3>Excel Integration</h3>
                  <p>Projects are automatically extracted when Excel files with project sheets are uploaded</p>
                  <span className="feature-status">Auto-processing enabled</span>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon">📊</div>
                <div className="feature-content">
                  <h3>Live Data</h3>
                  <p>No fallback data - all projects come from actual uploaded documents</p>
                  <span className="feature-status">Real-time updates</span>
                </div>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🎯</div>
                <div className="feature-content">
                  <h3>Ward Matching</h3>
                  <p>Projects are automatically matched to wards using flexible name matching</p>
                  <span className="feature-status">Smart assignment</span>
                </div>
              </div>
            </div>
          </section>

          <section className="projects-instructions">
            <div className="section-header">
              <h2>How to Upload Projects</h2>
            </div>
            <div className="upload-instructions">
              <div className="instruction-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Prepare Excel File</h3>
                  <p>Create an Excel file with a sheet containing project data. Include columns for:</p>
                  <ul>
                    <li>Project Name/Title</li>
                    <li>Ward/Location</li>
                    <li>Sector/Category</li>
                    <li>Budget/Cost</li>
                    <li>Contractor (optional)</li>
                    <li>Status (optional)</li>
                    <li>Progress % (optional)</li>
                  </ul>
                </div>
              </div>
              
              <div className="instruction-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Upload via Institutional Responder</h3>
                  <p>Go to the Institutional Responder dashboard and upload your Excel file as a budget document. The system will:</p>
                  <ul>
                    <li>Detect project sheets automatically</li>
                    <li>Extract project information</li>
                    <li>Match projects to wards</li>
                    <li>Create project records</li>
                  </ul>
                </div>
              </div>
              
              <div className="instruction-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>View Results</h3>
                  <p>After upload, projects will appear in:</p>
                  <ul>
                    <li>Projects & Analytics Dashboard</li>
                    <li>Public projects page</li>
                    <li>Ward-specific project lists</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="projects-status">
            <div className="section-header">
              <h2>Extracted Projects ({projects.length})</h2>
            </div>
            
            {projects.length === 0 ? (
              <div className="status-message">
                <div className="status-icon">📊</div>
                <div className="status-content">
                  <h3>Live System Demo</h3>
                  <p>This system contains no fallback or sample data. All projects displayed come from actual Excel uploads.</p>
                  <p>To see projects here, upload an Excel file with project data through the Institutional Responder dashboard.</p>
                  <div className="status-actions">
                    <p><strong>Sample file available:</strong> sample_budget_projects.xlsx in the project directory</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="projects-grid">
                {projects.map(project => {
                  const ward = wards.find(w => w.id === project.ward_id);
                  return (
                    <div key={project.id} className="project-card">
                      <div className="project-header">
                        <h3>{project.name}</h3>
                        <span className={`status-badge ${project.status}`}>
                          {project.status.toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="project-info">
                        <p><strong>📍 Location:</strong> {ward?.name || 'Unknown'} Ward</p>
                        <p><strong>🏢 Sector:</strong> {project.sector}</p>
                        <p><strong>💰 Budget:</strong> KES {(project.budget / 1e6).toFixed(1)}M</p>
                        {project.contractor && (
                          <p><strong>🏗️ Contractor:</strong> {project.contractor}</p>
                        )}
                        <p><strong>📅 Created:</strong> {new Date(project.created_at).toLocaleDateString()}</p>
                        <p><strong>📝 Source:</strong> Excel Upload</p>
                      </div>

                      <div className="progress-section">
                        <div className="progress-header">
                          <span>Progress: {project.progress}%</span>
                        </div>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${project.progress}%` }}
                          ></div>
                        </div>
                      </div>

                      {project.description && (
                        <div className="project-description">
                          <p>{project.description}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      <section className="document-stats">
        <div className="section-header">
          <h2>Repository Statistics</h2>
        </div>
        <div className="stats-grid">
          <div className="stat-card">
            <h3><div className="icon icon-sm icon-primary">📄</div>Total Documents</h3>
            <p className="stat-value">{documents.length}</p>
          </div>
          <div className="stat-card">
            <h3><div className="icon icon-sm icon-success">🏗️</div>Sectors Covered</h3>
            <p className="stat-value">{new Set(documents.map(d => d.sector)).size}</p>
          </div>
          <div className="stat-card">
            <h3><div className="icon icon-sm icon-warning">🆕</div>Added This Month</h3>
            <p className="stat-value">{documents.filter(d => new Date(d.created_at) > new Date(Date.now() - 30*24*60*60*1000)).length}</p>
          </div>
          <div className="stat-card">
            <h3><div className="icon icon-sm icon-danger">💾</div>Total Size</h3>
            <p className="stat-value">{Math.round(documents.reduce((sum, d) => sum + (d.file_size || 0), 0) / 1024 / 1024)}MB</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DocumentsPage;